import React, { useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import { justiceSphereLabel } from '../../utils/justiceSphere';

function normalizeSigla(sigla) {
  return String(sigla || '').replace(/\s+/g, '').trim().toUpperCase();
}

export default function TribunalPicker({
  tribunals = [],
  selectedSiglas = [],
  justiceTypes = [],
  onChange,
  compact = false,
  disabled = false,
}) {
  const [query, setQuery] = useState('');
  const selectedSet = useMemo(
    () => new Set(selectedSiglas.map(normalizeSigla)),
    [selectedSiglas],
  );

  const available = useMemo(() => {
    const selectedTypes = new Set(justiceTypes);
    return (tribunals || [])
      .filter((item) => item.active !== false)
      .filter((item) => {
        if (selectedTypes.size === 0) return true;
        return selectedTypes.has(item.justiceType);
      })
      .sort((a, b) => a.sigla.localeCompare(b.sigla, 'pt-BR', { numeric: true }));
  }, [tribunals, justiceTypes]);

  const visible = useMemo(() => {
    const term = query.trim().toUpperCase();
    if (!term) return available;
    return available.filter((item) => {
      const haystack = `${item.sigla} ${item.name || ''} ${justiceSphereLabel(item.justiceType, 'shortLabel') || ''}`.toUpperCase();
      return haystack.includes(term);
    });
  }, [available, query]);

  const selectedItems = available.filter((item) => selectedSet.has(normalizeSigla(item.sigla)));

  function toggle(sigla) {
    if (disabled) return;
    const normalized = normalizeSigla(sigla);
    if (selectedSet.has(normalized)) {
      onChange(selectedSiglas.filter((item) => normalizeSigla(item) !== normalized));
      return;
    }
    onChange([...selectedSiglas.map(normalizeSigla), normalized]);
  }

  function selectVisible() {
    if (disabled) return;
    const next = new Set(selectedSet);
    visible.forEach((item) => next.add(normalizeSigla(item.sigla)));
    onChange([...next]);
  }

  function clearAll() {
    if (disabled) return;
    onChange([]);
  }

  return (
    <div className="space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className={`font-medium text-gray-800 ${compact ? 'text-xs' : 'text-sm'}`}>
            Tribunais
          </p>
          <p className="text-[11px] text-gray-500">
            {selectedSiglas.length} selecionado(s) · {available.length} disponíveis
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs shrink-0">
          <button
            type="button"
            onClick={selectVisible}
            disabled={disabled || visible.length === 0}
            className="text-blue-600 hover:text-blue-800 disabled:text-gray-400"
          >
            Selecionar visíveis
          </button>
          <button
            type="button"
            onClick={clearAll}
            disabled={disabled || selectedSiglas.length === 0}
            className="text-red-600 hover:text-red-800 disabled:text-gray-400"
          >
            Limpar todos
          </button>
        </div>
      </div>

      <div className="relative">
        <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Buscar tribunal..."
          disabled={disabled}
          className={`w-full pl-8 pr-3 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            compact ? 'py-1 text-xs' : 'py-2 text-sm'
          }`}
        />
      </div>

      {selectedItems.length > 0 && (
        <div className="border border-gray-200 rounded-lg p-2 bg-gray-50">
          <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-1.5">
            Tribunais desta configuração
          </p>
          <div className="flex flex-wrap gap-1">
            {selectedItems.map((item) => (
              <button
                key={item.sigla}
                type="button"
                onClick={() => toggle(item.sigla)}
                disabled={disabled}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 text-[11px] font-medium"
              >
                {item.sigla}
                <X className="w-3 h-3" />
              </button>
            ))}
          </div>
        </div>
      )}

      <div className={`flex flex-wrap gap-1.5 overflow-y-auto ${compact ? 'max-h-32' : 'max-h-48'}`}>
        {visible.length === 0 ? (
          <p className="text-xs text-gray-400 py-2">
            {available.length === 0
              ? 'Nenhum tribunal ativo para as esferas selecionadas.'
              : 'Nenhum tribunal encontrado para a busca.'}
          </p>
        ) : (
          visible.map((item) => {
            const selected = selectedSet.has(normalizeSigla(item.sigla));
            return (
              <button
                key={item.id || item.sigla}
                type="button"
                onClick={() => toggle(item.sigla)}
                disabled={disabled}
                className={`rounded-full border px-2.5 py-1 text-left transition-colors ${
                  selected
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'bg-white border-gray-300 text-gray-800 hover:border-blue-400'
                } ${compact ? 'text-[11px]' : 'text-xs'}`}
              >
                <span className="font-semibold">{item.sigla}</span>
                <span className={`ml-1 ${selected ? 'text-blue-100' : 'text-gray-500'}`}>
                  {justiceSphereLabel(item.justiceType, 'shortLabel') || item.justiceType}
                </span>
              </button>
            );
          })
        )}
      </div>

      <p className="text-[11px] text-gray-500">
        Sem tribunal selecionado, a regra vale para todos os tribunais das esferas marcadas.
      </p>
    </div>
  );
}
